import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';

import type {
  HtmCodeGroupId,
} from '@bindoc-projects/hartmann-domain-lib';
import {
  HTM_CODE_GROUPS,
} from '@bindoc-projects/hartmann-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
  FilterDetailedText,
} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionPluginImpl
} from '@bindoc-projects/filter-condition-domain-lib';

export const HARTMANN_GROUP_CONDITION_ID = 'hartmannGroup';
export const FilterConditionHartmannCodeGroupIdsPluginType: FilterConditionPluginSpecification<
  HtmCodeGroupId[],
  JsoFilterConditionHartmannCodeGroupIdsPluginDto,
  HtmHartmannGroupCondition
> = {
  type: 'hartmannCodeGroupIds',
  category: 'case',
  displayName: { de: 'Hartmann Gruppen' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: JsoFilterConditionHartmannCodeGroupIdsPluginDto,
  ): FilterConditionPluginImpl<HtmCodeGroupId[]> {
    return new FilterConditionHartmannCodeGroupIdsPlugin(
        obj.hartmannCodeGroupIds as HtmCodeGroupId[],
      );
  },
  fromCondition(
    jsonObj: HtmHartmannGroupCondition,
  ): FilterConditionPluginImpl<HtmCodeGroupId[]> {
    return new FilterConditionHartmannCodeGroupIdsPlugin(
        (jsonObj.includes ?? []) as HtmCodeGroupId[],
      );
  },
  newInstance(): FilterConditionPluginImpl<HtmCodeGroupId[]> {
    return new FilterConditionHartmannCodeGroupIdsPlugin([]);
  },
};

export class FilterConditionHartmannCodeGroupIdsPlugin extends FilterConditionPluginImpl<
  HtmCodeGroupId[],
  JsoFilterConditionHartmannCodeGroupIdsPluginDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    HtmCodeGroupId[],
    JsoFilterConditionHartmannCodeGroupIdsPluginDto,
    HtmHartmannGroupCondition
  > = FilterConditionHartmannCodeGroupIdsPluginType;

  constructor(private hartmannCodeGroupIds: HtmCodeGroupId[]) {
    super(FilterConditionHartmannCodeGroupIdsPluginType.type);
  }

  clone(): FilterConditionHartmannCodeGroupIdsPlugin {
    return new FilterConditionHartmannCodeGroupIdsPlugin([
      ...this.hartmannCodeGroupIds,
    ]);
  }

  override equals(other: FilterConditionHartmannCodeGroupIdsPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.hartmannCodeGroupIds, other.hartmannCodeGroupIds)
    );
  }

  override validationToText(): string {
    if (this.hartmannCodeGroupIds.length === 0) {
      return 'Mindestens eine Hartmann Gruppe auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new HtmHartmannGroupCondition(this.hartmannCodeGroupIds, []);
  }

  toJSON(): JsoFilterConditionHartmannCodeGroupIdsPluginDto | null {
    return {
      type: this.type,
      hartmannCodeGroupIds: this.hartmannCodeGroupIds,
    };
  }

  toText(): string | null {
    return this.hartmannCodeGroupIds.length === 1
      ? 'eine Hartmann Gruppe'
      : `${this.hartmannCodeGroupIds.length} Hartmann Gruppen`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.hartmannCodeGroupIds.map((id) => {
      const group = HTM_CODE_GROUPS.find((g) => g.id === id);
      return group ? group.displayName : id;
    });
    return { title, items };
  }

  toValue(): HtmCodeGroupId[] {
    return this.hartmannCodeGroupIds;
  }
}

export class HtmHartmannGroupCondition extends BdContentCondition<string> {
  constructor(includes: HtmCodeGroupId[], excludes: HtmCodeGroupId[]) {
    super(HARTMANN_GROUP_CONDITION_ID, includes, excludes);
  }
}

interface JsoFilterConditionHartmannCodeGroupIdsPluginDto {
  type: string;
  hartmannCodeGroupIds: string[];
}
